/**
 * 24K.Infinity — Recharts chart theme
 * -------------------------------------------------------------------------
 * Reproduces the reference's data-viz language: light ground, dashed grey
 * gridlines, thin cobalt series, black scatter, % axes, tabular labels and
 * a minimal Swiss tooltip. Pass the live MUI theme; spread the results onto
 * recharts primitives.
 *
 *   const c = getChartTheme(theme);
 *   <CartesianGrid {...c.grid} />
 *   <XAxis {...c.axis} dataKey="t" />
 *   <Line {...c.line(0)} dataKey="v" />
 * -------------------------------------------------------------------------
 */
import { fontFamily, numeric, radius, shadow } from './tokens';

export function getChartTheme(theme) {
  const s = theme.palette.surface;
  const series = theme.dataviz.series;
  const isDark = theme.palette.mode === 'dark';
  const gridColor = isDark ? theme.dataviz.gridDark : theme.dataviz.gridLight;

  const tickStyle = {
    fill: s.text.secondary,
    fontSize: 11,
    fontFamily: fontFamily.sans,
    fontVariantNumeric: numeric.fontVariantNumeric,
  };

  return {
    series,
    seriesColor: (i) => series[i % series.length],

    // Light, dashed horizontal gridlines only — no vertical noise.
    grid: {
      stroke: gridColor,
      strokeDasharray: '4 6',
      vertical: false,
    },

    axis: {
      stroke: s.border,
      tick: tickStyle,
      tickLine: false,
      axisLine: { stroke: s.border },
    },

    // Thin hairline series, soft dot on active only.
    line: (i = 0) => ({
      type: 'monotone',
      stroke: series[i % series.length],
      strokeWidth: 1.5,
      dot: false,
      activeDot: { r: 4, strokeWidth: 0 },
    }),

    // Crisp flat bars, square corners.
    bar: (i = 0) => ({
      fill: series[i % series.length],
      radius: [0, 0, 0, 0],
      maxBarSize: 28,
    }),

    // Soft area under a cobalt line.
    area: (i = 0) => ({
      type: 'monotone',
      stroke: series[i % series.length],
      strokeWidth: 1.5,
      fillOpacity: 1,
    }),

    scatter: { fill: theme.palette.text.primary },

    // A faint cobalt highlight band (reference "risk period" motif).
    referenceBand: {
      fill: theme.palette.primary.main,
      fillOpacity: isDark ? 0.18 : 0.08,
      strokeWidth: 0,
    },

    tooltip: {
      cursor: { stroke: s.borderStrong, strokeWidth: 1 },
      contentStyle: {
        background: s.paper,
        border: `1px solid ${s.border}`,
        borderRadius: radius.md,
        boxShadow: isDark ? shadow.darkMd : shadow.md,
        fontFamily: fontFamily.sans,
        fontSize: 12,
        color: s.text.primary,
      },
      labelStyle: { color: s.text.secondary, fontSize: 11, marginBottom: 4 },
      itemStyle: { color: s.text.primary, fontSize: 12, padding: 0 },
    },

    // SVG gradient defs id helper for area fills.
    gradientId: (i = 0) => `infinity-grad-${i}`,
    gradientStops: (i = 0) => [
      { offset: '0%', stopColor: series[i % series.length], stopOpacity: isDark ? 0.34 : 0.18 },
      { offset: '100%', stopColor: series[i % series.length], stopOpacity: 0 },
    ],
  };
}

export default getChartTheme;
